import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Package2, Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, X } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useThemeStore } from '../../stores/themeStore'
import { useUIStore } from '../../stores/uiStore'
import { cn } from '../../lib/utils'
import toast from 'react-hot-toast'

export default function Login() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuthStore()
  const { resolvedTheme } = useThemeStore()
  const { setGlobalLoading } = useUIStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!email) newErrors.email = 'ایمیل یا نام کاربری الزامی است'
    if (!password) newErrors.password = 'رمز عبور الزامی است'
    else if (password.length < 4) newErrors.password = 'رمز عبور باید حداقل ۴ کاراکتر باشد'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    if (!validate()) return

    setGlobalLoading(true)
    try {
      await login(email, password, rememberMe)
      toast.success('ورود موفقیت‌آمیز بود')
      navigate('/')
    } catch (err: any) {
      const message = err.response?.data?.detail || 'ورود ناموفق بود'
      toast.error(message)
    } finally {
      setGlobalLoading(false)
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-primary-600 flex items-center justify-center">
              <Package2 className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-neutral-900 dark:text-white">Kaseb</span>
          </Link>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">خوش آمدید</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">به سامانه مدیریت کسب‌وکار Kaseb وارد شوید</p>
        </div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-800 p-8"
        >
          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-6 p-4 rounded-xl bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 flex items-start gap-3"
              role="alert"
            >
              <AlertCircle className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-danger-800 dark:text-danger-300">خطای ورود</p>
                <p className="text-sm text-danger-700 dark:text-danger-400 mt-1">{error}</p>
              </div>
              <button
                onClick={clearError}
                className="text-danger-500 hover:text-danger-700 dark:hover:text-danger-300"
                aria-label="بستن خطا"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Quick Login Role Selector */}
          <div className="mb-6 p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700 space-y-2">
            <p className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
              ورود سریع با نمونه پرسنل و نقش‌های تعریف شده:
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => { setEmail('admin@kaseb.app'); setPassword('123456'); }}
                className="px-2.5 py-1 rounded-lg bg-white dark:bg-neutral-900 text-[11px] font-bold text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 hover:border-primary-500"
              >
                👑 مدیر سیستم (کامل)
              </button>
              <button
                type="button"
                onClick={() => { setEmail('karimi@kaseb.app'); setPassword('123456'); }}
                className="px-2.5 py-1 rounded-lg bg-white dark:bg-neutral-900 text-[11px] font-bold text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 hover:border-primary-500"
              >
                💳 صندوق‌دار (فروش)
              </button>
              <button
                type="button"
                onClick={() => { setEmail('mohammadi@kaseb.app'); setPassword('123456'); }}
                className="px-2.5 py-1 rounded-lg bg-white dark:bg-neutral-900 text-[11px] font-bold text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 hover:border-primary-500"
              >
                📦 انباردار (انبار)
              </button>
              <button
                type="button"
                onClick={() => { setEmail('rezaei@kaseb.app'); setPassword('123456'); }}
                className="px-2.5 py-1 rounded-lg bg-white dark:bg-neutral-900 text-[11px] font-bold text-neutral-800 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-700 hover:border-primary-500"
              >
                📊 حسابدار (مالی)
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                آدرس ایمیل
              </label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" aria-hidden="true" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => {
                    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                      setErrors(prev => ({ ...prev, email: 'فرمت ایمیل نامعتبر است' }))
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.email; return n; })
                    }
                  }}
                  className={cn(
                    'w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200',
                    'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400',
                    errors.email
                      ? 'border-danger-500 focus:ring-2 focus:ring-danger-500/20 focus:border-danger-500'
                      : 'border-neutral-300 dark:border-neutral-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500'
                  )}
                  placeholder="example@company.com"
                  autoComplete="email"
                  disabled={isLoading}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1.5 text-sm text-danger-600 dark:text-danger-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  رمز عبور
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  فراموشی رمز عبور؟
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" aria-hidden="true" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => {
                    if (password && password.length < 8) {
                      setErrors(prev => ({ ...prev, password: 'رمز عبور باید حداقل ۸ کاراکتر باشد' }))
                    } else {
                      setErrors(prev => { const n = { ...prev }; delete n.password; return n; })
                    }
                  }}
                  className={cn(
                    'w-full pl-10 pr-12 py-3 rounded-xl border transition-all duration-200',
                    'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400',
                    errors.password
                      ? 'border-danger-500 focus:ring-2 focus:ring-danger-500/20 focus:border-danger-500'
                      : 'border-neutral-300 dark:border-neutral-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500'
                  )}
                  placeholder="●●●●●●●●"
                  autoComplete="current-password"
                  disabled={isLoading}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                  aria-label={showPassword ? 'مخفی کردن رمز' : 'نمایش رمز'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1.5 text-sm text-danger-600 dark:text-danger-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-700 text-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-neutral-900"
                />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">مرا به خاطر بسپار</span>
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full py-3 rounded-xl font-medium text-sm transition-all duration-200',
                'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  در حال ورود...
                </>
              ) : (
                <>
                  <span>ورود به سیستم</span>
                  <CheckCircle className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200 dark:border-neutral-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">یا</span>
            </div>
          </div>

          {/* Register Link */}
          <p className="text-center text-sm text-neutral-600 dark:text-neutral-400">
            حساب کاربری ندارید؟{' '}
            <Link to="/register" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
              ثبت‌نام رایگان
            </Link>
          </p>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-8 grid grid-cols-3 gap-4 text-center"
        >
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">امنیت بالا</p>
          </div>
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-success-100 dark:bg-success-900/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">چند کاربره</p>
          </div>
          <div className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-xl bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center mx-auto mb-2">
              <Package2 className="w-5 h-5 text-warning-600 dark:text-warning-400" />
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">رایگان برای همیشه</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}